/* eslint-disable react/prop-types */
/* eslint-disable no-nested-ternary */
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { cards as CardsApi, statement } from '../../helpers/api';
import cardTypes from '../../helpers/cardTypes';
import { Tabs, MonetaryTable, Modal } from '../../components';
import { Button } from '../../elements';
import Spinner from '../../elements/Spinner';
import dashboardColors from '../../helpers/dashboardColors';
import devices from '../../styles/devices';
import CustomDate from './CustomDate';
import getCompanyFirstNameByIssuer from '~/helpers/getCompanyFirstNameByIssuer';

const Container = styled.div`
  max-width: 750px;
  margin: 0 auto;
`;
const ContainerButton = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 21px;
  margin-top: 28px;

  @media (min-width: ${devices.tablet}) {
    display: none;
  }
`;
const ViewMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 10px 0;
  &.only-desktop {
    @media (max-width: ${devices.tablet}) {
      display: none;
    }
  }
`;

const EmptyMsg = styled.p`
  text-align: center;
  padding: 40px 0;
  font-size: 1.4em;
`;

const ModalContainer = styled.div`
  background: ${({ theme, type }) => dashboardColors(theme, type).color3};
  padding: 28px 21px 0;
`;

const ModalContent = styled.div`
  margin: 0 -21px;
`;

const Title = styled.h2`
  font-weight: bold;
  font-size: 26px;
  align-self: flex-start;
  color: ${({ theme }) => theme.colors.white};
  margin-bottom: 25px;
  margin-left: 1px;
  span {
    text-transform: capitalize;
  }
`;

const Loading = () => (
  <div style={{ textAlign: 'center' }}>
    <Spinner />
  </div>
);

const emptyMsg = '🙁 Você ainda não tem lançamentos no período';

// eslint-disable-next-line react/prop-types
const Table = ({
  data,
  card,
  isLoading,
  isTotal,
  viewMore,
  mobile,
  ariaLabel,
}) => (
  <MonetaryTable data={data} mobile={mobile} ariaLabel={ariaLabel}>
    <ViewMoreContainer className={`${!mobile ? 'only-desktop' : ''}`}>
      {isLoading ? (
        <Spinner size={60} />
      ) : (
        !isTotal && (
          <Button
            // eslint-disable-next-line react/prop-types
            color={card?.tipo ? 'caixa' : card?.codigoProduto}
            variant="outlined"
            label="Ver mais lançamentos"
            onClick={viewMore}
            ariaLabel="Ver mais lançamentos"
          />
        )
      )}
    </ViewMoreContainer>
  </MonetaryTable>
);

const TransactionsTable = ({
  mobile,
  card,
  user,
  isSlideActive,
  labels = ['15 dias', '30 dias', '60 dias', 'Selecionar data'],
}) => {
  const [transactions15, setTransactions15] = useState();
  const [page15, setPage15] = useState();
  const [totalPages15, setTotalPages15] = useState(0);
  const [loading15, setLoading15] = useState(false);
  const [transactions30, setTransactions30] = useState();
  const [page30, setPage30] = useState();
  const [totalPages30, setTotalPages30] = useState(0);
  const [loading30, setLoading30] = useState(false);
  const [transactions60, setTransactions60] = useState();
  const [page60, setPage60] = useState();
  const [totalPages60, setTotalPages60] = useState(0);
  const [loading60, setLoading60] = useState(false);
  const [transactionsCustomDate, setTransactionsCustomDate] = useState();
  const [pageCustomDate, setPageCustomDate] = useState();
  const [totalPagesCustomDate, setTotalPagesCustomDate] = useState(0);
  const [loadingCustomDate, setLoadingCustomDate] = useState(false);
  const [customDates, setCustomDates] = useState({
    startDate: '',
    endDate: '',
  });

  const getTransactions = useCallback(
    (page, days) =>
      CardsApi.transactions(
        user.cpf,
        card.tokenCartao,
        card.numeroConta,
        days, // days
        page, // page
        20, // items per page
      ),
    [card, user],
  );

  function convertDate(date) {
    return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD');
  }

  async function getTransactionsByDate(payload = customDates) {
    const { startDate, endDate } = payload;

    try {
      const payload = {
        cpf: user?.cpf,
        tokenCard: card?.tokenCartao,
        numberAccount: card?.numeroConta,
        startDate: convertDate(startDate),
        endDate: convertDate(endDate),
        page: pageCustomDate,
        itemsPerPage: 20,
      };

      return statement.transactionsByDate(payload);
    } catch (e) {
      return e;
    }
  }

  async function handleCustomDate(payload) {
    if (!payload?.startDate || !payload?.endDate) {
      return;
    }

    setPageCustomDate(0);

    setCustomDates({
      startDate: payload?.startDate,
      endDate: payload?.endDate,
    });

    getTransactionsByDate(payload)
      .then(response => {
        if (!response.result) {
          throw new Error('no-content');
        }
        setTransactionsCustomDate([...response.result]);
        setTotalPagesCustomDate(response.pagination?.totalPages);
      })
      .catch(e => {
        setTransactionsCustomDate(
          e.message === 'no-content' ? emptyMsg : false,
        );
      })
      .finally(() => {
        setLoadingCustomDate(false);
      });
  }

  useEffect(() => {
    if (isSlideActive && page15 === undefined) {
      setPage15(0);
    }
    // eslint-disable-next-line
  }, [isSlideActive]);

  useEffect(() => {
    if (typeof page15 === 'number') {
      setLoading15(true);
      getTransactions(page15, 15)
        .then(response => {
          if (!response.result) {
            throw new Error('no-content');
          }
          const prevValue = transactions15 || [];
          setTransactions15([...prevValue, ...response.result]);
          setTotalPages15(response.pagination?.totalPages);
          setLoading15(false);
        })
        .catch(e => {
          setTransactions15(e.message === 'no-content' ? emptyMsg : false);
          setLoading15(false);
        });
    }
    // eslint-disable-next-line
  }, [page15]);

  useEffect(() => {
    if (typeof page30 === 'number') {
      setLoading30(true);
      getTransactions(page30, 30)
        .then(response => {
          if (!response.result) {
            throw new Error('no-content');
          }
          const prevValue = transactions30 || [];
          setTransactions30([...prevValue, ...response.result]);
          setTotalPages30(response.pagination?.totalPages);
          setLoading30(false);
        })
        .catch(e => {
          setTransactions30(e.message === 'no-content' ? emptyMsg : false);
          setLoading30(false);
        });
    }
    // eslint-disable-next-line
  }, [page30]);

  useEffect(() => {
    if (typeof page60 === 'number') {
      setLoading60(true);
      getTransactions(page60, 60)
        .then(response => {
          if (!response.result) {
            throw new Error('no-content');
          }
          const prevValue = transactions60 || [];
          setTransactions60([...prevValue, ...response.result]);
          setTotalPages60(response.pagination?.totalPages);
          setLoading60(false);
        })
        .catch(e => {
          setTransactions60(e.message === 'no-content' ? emptyMsg : false);
          setLoading60(false);
        });
    }
    // eslint-disable-next-line
  }, [page60]);

  useEffect(() => {
    if (
      typeof pageCustomDate === 'number' &&
      customDates?.startDate &&
      customDates?.endDate
    ) {
      setLoadingCustomDate(true);
      getTransactionsByDate()
        .then(response => {
          if (!response.result) {
            throw new Error('no-content');
          }
          const prevValue = transactionsCustomDate || [];
          setTransactionsCustomDate([...prevValue, ...response.result]);
          setTotalPagesCustomDate(response.pagination?.totalPages);
          setLoadingCustomDate(false);
        })
        .catch(e => {
          setTransactionsCustomDate(
            e.message === 'no-content' ? emptyMsg : false,
          );
          setLoadingCustomDate(false);
        });
    }
    // eslint-disable-next-line
  }, [pageCustomDate]);

  const handleCustomDateContent = () => {
    if (customDates?.startDate && customDates?.endDate) {
      return Array.isArray(transactionsCustomDate) ? (
        <Table
          ariaLabel="Transações para um período customizado"
          data={transactionsCustomDate}
          card={card}
          isTotal={pageCustomDate >= totalPagesCustomDate - 1}
          viewMore={() =>
            setPageCustomDate(
              (typeof pageCustomDate === 'number' ? pageCustomDate : -1) + 1,
            )
          }
          isLoading={loadingCustomDate}
          mobile={mobile}
        />
      ) : typeof transactionsCustomDate === 'string' ? (
        <EmptyMsg>{transactionsCustomDate}</EmptyMsg>
      ) : transactionsCustomDate === undefined ? (
        <Loading />
      ) : (
        'Erro ao capturar transações'
      );
    }

    return false;
  };

  return (
    <div className="extrato-step">
      <Tabs
        type={card?.tipo ? 'caixa' : card?.codigoProduto}
        title="extrato"
        labels={labels}
        mobile={mobile}
        data={[
          {
            id: 0,
            content: Array.isArray(transactions15) ? (
              <Table
                ariaLabel="Transações de 15 dias"
                data={transactions15}
                card={card}
                isTotal={page15 >= totalPages15 - 1}
                viewMore={() =>
                  setPage15((typeof page15 === 'number' ? page15 : -1) + 1)
                }
                isLoading={loading15}
                mobile={mobile}
              />
            ) : typeof transactions15 === 'string' ? (
              <EmptyMsg>{transactions15}</EmptyMsg>
            ) : transactions15 === undefined ? (
              <Loading />
            ) : (
              'Erro ao capturar transações'
            ),
          },
          {
            id: 1,
            content: Array.isArray(transactions30) ? (
              <Table
                ariaLabel="Transações de 30 dias"
                data={transactions30}
                card={card}
                isTotal={page30 >= totalPages30 - 1}
                viewMore={() =>
                  setPage30((typeof page30 === 'number' ? page30 : -1) + 1)
                }
                isLoading={loading30}
                mobile={mobile}
              />
            ) : typeof transactions30 === 'string' ? (
              <EmptyMsg>{transactions30}</EmptyMsg>
            ) : transactions30 === undefined ? (
              <Loading />
            ) : (
              'Erro ao capturar transações'
            ),
          },
          {
            id: 2,
            content: Array.isArray(transactions60) ? (
              <Table
                ariaLabel="Transações de 60 dias"
                data={transactions60}
                card={card}
                isTotal={page60 >= totalPages60 - 1}
                viewMore={() =>
                  setPage60((typeof page60 === 'number' ? page60 : -1) + 1)
                }
                isLoading={loading60}
                mobile={mobile}
              />
            ) : typeof transactions60 === 'string' ? (
              <EmptyMsg>{transactions60}</EmptyMsg>
            ) : transactions60 === undefined ? (
              <Loading />
            ) : (
              'Erro ao capturar transações'
            ),
          },
          {
            id: 3,
            content: (
              <>
                <CustomDate
                  type={card?.tipo ? 'caixa' : card?.codigoProduto}
                  onSubmit={handleCustomDate}
                />
                {handleCustomDateContent()}
              </>
            ),
          },
        ]}
        onChange={activeTab => {
          if (activeTab === 1 && page30 === undefined) {
            setPage30(0);
          }
          if (activeTab === 2 && page60 === undefined) {
            setPage60(0);
          }
          if (activeTab === 3 && pageCustomDate === undefined) {
            setPageCustomDate(0);
          }
        }}
      />
    </div>
  );
};

export default function Transactions({ card, isSlideActive }) {
  const [openModal, setOpenModal] = useState(false);
  const user = useSelector(state => state.user);
  const colorCode = card?.tipo ? 'caixa' : card?.codigoProduto;
  const issuerName = useSelector(state => state?.issuer?.name);
  const companyFirstName = getCompanyFirstNameByIssuer(issuerName);

  return (
    <Container>
      <TransactionsTable
        card={card}
        user={user}
        isSlideActive={isSlideActive}
      />
      <ContainerButton>
        <Button
          fullWidth
          color={colorCode}
          variant="outlined"
          label="Ver extrato por data"
          ariaLabel="Ver extrato por data"
          onClick={() => setOpenModal(true)}
        />
      </ContainerButton>
      <Modal handleClose={() => setOpenModal(false)} open={openModal} fullWidth>
        <ModalContainer type={colorCode}>
          <Title>
            {companyFirstName} <span>{cardTypes[card?.codigoProduto]}</span>
          </Title>
          <ModalContent>
            <TransactionsTable
              card={card}
              user={user}
              isSlideActive={isSlideActive}
              mobile
            />
          </ModalContent>
        </ModalContainer>
      </Modal>
    </Container>
  );
}

Transactions.propTypes = {
  card: PropTypes.object,
  isSlideActive: PropTypes.bool.isRequired,
};
